import {formatDuration, intervalToDuration} from 'date-fns';
import {StreamEvent} from '../types';
import {BaseStreamObserver} from './base-stream-observer';

/**
 * StreamTimer is a {@link StreamObserver} implementation that captures timing metrics
 * for every LangGraph node that emits a chunk. It records:
 * 1. Total stream time (from first chunk to stream end).
 * 2. Per-node durations (time between the first and last chunk for that node).
 *
 * Consumers can retrieve metrics via:
 *   • {@link getTotalStreamTime} – overall stream duration in milliseconds.
 *   • {@link getNodeDurations} – map of node name -> duration (ms).
 *   • {@link getFinalTimingValues} – convenience wrapper returning both.
 */

interface StreamTimerOptions {
  startNode?: string;
  endNode?: string;
}

export class StreamTimer extends BaseStreamObserver {
  public streamStartTime = 0;
  public streamEndTime = 0;

  public name = 'streamTimer';

  /**
   * Internal map that stores the first and last timestamp (in ms) for each node.
   * The duration for any given node is computed as {@code last - first}.
   */
  private readonly nodeTimings = new Map<
    string,
    {start: number; end: number}
  >();

  private readonly startNode?: string;
  private readonly endNode?: string;

  constructor(options: StreamTimerOptions = {}) {
    super();
    this.startNode = options.startNode;
    this.endNode = options.endNode;
  }

  onStart(): void {
    if (!this.startNode) {
      this.streamStartTime = Date.now();
    }
  }

  onChunk(payload: StreamEvent): void {
    try {
      if (payload.event === 'on_custom_event') {
        const node = payload.metadata?.langgraph_node;
        if (typeof node !== 'string') {
          return;
        }

        // Track per-node timings for every node encountered
        const existing = this.nodeTimings.get(node);

        const now = Date.now();

        if (!existing) {
          this.nodeTimings.set(node, {start: now, end: now});
        } else {
          // Update the end time whenever we see another chunk for the same node
          existing.end = now;
        }

        // If an endNode is specified, mark stream end when encountered
        if (this.endNode && node === this.endNode) {
          this.streamEndTime = Date.now();
        }

        if (this.streamStartTime === 0) {
          if (!this.startNode || node === this.startNode) {
            this.streamStartTime = Date.now();
          }
        }
      }
    } catch (error) {
      console.error('Error parsing chunk:', error);
    }
  }

  onEnd(): void {
    this.streamEndTime = Date.now();
  }

  public getTotalStreamTime(): number {
    if (this.getStreamState() === 'active') {
      return Date.now() - this.streamStartTime;
    }
    return this.streamEndTime - this.streamStartTime;
  }

  public formatTime(milliseconds: number | undefined): string {
    if (milliseconds === undefined || milliseconds <= 0) {
      return 'N/A';
    }

    const duration = intervalToDuration({
      start: 0,
      end: milliseconds,
    });

    return formatDuration(duration, {
      format: ['minutes', 'seconds'],
      zero: false,
      delimiter: ', ',
    });
  }

  public getData(): {
    streamTime: string;
    nodeDurations: Record<string, string>;
  } {
    const nodeDurations: Record<string, string> = {};
    this.nodeTimings.forEach((timing, name) => {
      nodeDurations[name] = (timing.end - timing.start).toString();
    });

    return {
      streamTime: this.getTotalStreamTime().toString(),
      nodeDurations,
    };
  }

  public async getFinalTimingValues(): Promise<{
    totalStreamTime: number;
    nodeDurations: Record<string, number>;
    totalReasoningTime: number;
  }> {
    // Wait for stream to end if still active, with timeout
    const maxWaitTime = 30000;
    const pollInterval = 50;
    const maxRetries = maxWaitTime / pollInterval;
    let retries = 0;

    while (this.getStreamState() === 'active' && retries < maxRetries) {
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
      retries++;
    }

    const nodeDurations = this.getNodeDurations();
    const totalReasoningTime = nodeDurations.reasoning ?? 0;

    return {
      totalStreamTime: this.getTotalStreamTime(),
      nodeDurations,
      totalReasoningTime,
    };
  }

  /**
   * Returns an object containing the duration (in ms) for every tracked node.
   */
  public getNodeDurations(): Record<string, number> {
    const results: Record<string, number> = {};
    this.nodeTimings.forEach((timing, name) => {
      results[name] = timing.end - timing.start;
    });
    return results;
  }
}
